import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, EmptyState } from '@/shared/components';
import { useApiProblem, type BookableResourceResponse } from '@/shared/api';
import { ResourceFormSheet } from './ResourceFormSheet';
import { ResourceList } from './ResourceList';
import { resourceLoadFailureText } from './resourceProblemText';
import { useResources } from './useResources';

type Editing = { kind: 'new' } | { kind: 'edit'; resource: BookableResourceResponse } | null;

/**
 * Ressourcen eines Objekts: exklusiv buchbare Räume wie Seminar- oder Gemeinschaftsraum. Ein
 * Ladefehler ist nie „keine Ressourcen“ — die Fläche sagt, dass nicht geladen werden konnte.
 */
export function ResourceAdministration({
  groupId,
  canEdit,
}: {
  groupId: string;
  canEdit: boolean;
}) {
  const resources = useResources(groupId);
  const problem = useApiProblem(resources.error);
  const [editing, setEditing] = useState<Editing>(null);
  const data = resources.data;

  return (
    <section
      className="border-line/50 bg-card shadow-card rounded-lg border"
      aria-busy={resources.isPending}
    >
      <div className="border-line/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-ink font-serif text-lg">Buchbare Ressourcen</h3>
          <p className="text-ink-soft mt-0.5 text-sm">
            {data
              ? `Zeiten in ${data.timezone} · Währung ${data.currency}`
              : 'Zeiten in der Zeitzone des Objekts'}
          </p>
        </div>
        {canEdit && data && data.resources.length > 0 && (
          <Button
            variant="brand"
            className="min-h-11"
            leftIcon={<Plus size={16} aria-hidden />}
            onClick={() => setEditing({ kind: 'new' })}
          >
            Ressource anlegen
          </Button>
        )}
      </div>
      {resources.isPending && (
        <p className="text-ink-soft px-5 py-6 text-sm">Ressourcen werden geladen …</p>
      )}
      {resources.isError && !data && (
        <div role="alert" className="px-5 py-6 text-sm">
          <p className="text-ink font-semibold">Die Ressourcen konnten nicht geladen werden.</p>
          <p className="text-ink-soft mt-1">
            {problem ? resourceLoadFailureText(problem) : 'Der Grund wird ermittelt …'}
          </p>
          <Button
            variant="ghost"
            className="mt-3 min-h-11"
            onClick={() => void resources.refetch()}
          >
            Erneut laden
          </Button>
        </div>
      )}
      {resources.isError && data && (
        <p role="status" className="text-ink-soft px-5 pt-3 text-sm">
          Nachladen fehlgeschlagen – zu sehen ist der zuletzt geladene Stand.
        </p>
      )}
      {data && data.resources.length === 0 && (
        <EmptyState
          icon="Building"
          title="Noch keine Ressource"
          description="Ohne aktive Ressource bleiben Gastportal und Belegung unverändert."
          action={
            canEdit
              ? {
                  label: 'Ressource anlegen',
                  size: 'lg',
                  onClick: () => setEditing({ kind: 'new' }),
                }
              : undefined
          }
        />
      )}
      {data && data.resources.length > 0 && (
        <ResourceList
          resources={data.resources}
          canEdit={canEdit}
          onEdit={(resource) => setEditing({ kind: 'edit', resource })}
        />
      )}
      {editing && data && (
        <ResourceFormSheet
          groupId={groupId}
          timezone={data.timezone}
          currency={data.currency}
          minimum={data.minimumChargeAmount}
          resource={editing.kind === 'edit' ? editing.resource : undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}
